import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Tables, TablesInsert, TablesUpdate } from "~/api/tables";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { db } from "~/api/db";
import {
  documentReferenceFields,
  requireText,
  rootKey,
  workspaceKey,
  textOrNull,
  todayIsoDate,
  useWorkspaceMutation,
  type Invoice,
  type InvoiceAmendment,
  type InvoiceItem,
  type InvoicePayment,
  type InvoiceRefund,
  type TimeEntry,
} from "./shared";

function parsePositiveAmount(value: string, label: string) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error(`${label} must be greater than zero.`);
  }
  return amount;
}

function parseGstRate(value: string) {
  const gstRate = Number(value);
  if (!Number.isFinite(gstRate) || gstRate < 0 || gstRate > 100) {
    throw new Error("GST rate must be between 0 and 100.");
  }
  return gstRate;
}

function parsePositiveQuantity(value: string) {
  const quantity = Number(value);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    throw new Error("Quantity must be greater than zero.");
  }
  return quantity;
}

export function useWorkspaceInvoices(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("invoices", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as Invoice[];
      const { data, error } = await db
        .from("invoices")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Invoice[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useWorkspaceMutation(
    workspaceId,
    "invoices",
    async (input: Record<string, string>, targetWorkspaceId) => {
      const dueDate = requireText(input.dueDate, "Due date");
      const clientId = textOrNull(input.clientId);
      const references = documentReferenceFields(input);
      const { data, error } = await db
        .from("invoices")
        .insert({
          workspace_id: targetWorkspaceId,
          client_id: clientId,
          status: "draft",
          due_date: dueDate,
          notes: textOrNull(input.notes),
          ...references,
        } satisfies TablesInsert<"invoices">)
        .select("*")
        .single();
      if (error) throw error;
      return data as Invoice;
    },
  );

  const updateStatus = useMutation({
    mutationFn: async (input: {
      id: string;
      status:
        "draft" | "sent" | "viewed" | "paid" | "overdue" | "void" | "refunded";
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const now = new Date().toISOString();
      const patch: TablesUpdate<"invoices"> = {
        status: input.status,
      };

      if (input.status === "sent") patch.sent_at = now;
      if (input.status === "paid") {
        patch.paid_at = now;
        patch.sent_at = now;
      }
      if (input.status === "draft") {
        patch.sent_at = null;
        patch.paid_at = null;
      }
      if (input.status === "void") patch.paid_at = null;

      const { error } = await db
        .from("invoices")
        .update(patch)
        .eq("id", input.id)
        .eq("workspace_id", workspaceId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("invoices", workspaceId),
      });
    },
  });

  const update = useMutation({
    mutationFn: async (input: {
      id: string;
      clientId?: string;
      dueDate: string;
      notes?: string;
      amendmentReason?: string;
      purchaseOrderReference?: string;
      clientReference?: string;
      jobStartsOn?: string;
      jobEndsOn?: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      await unwrap(
        api.updateInvoiceDetails({
          path: { id: input.id },
          body: {
            amendment_reason: textOrNull(input.amendmentReason) ?? undefined,
            client_id: textOrNull(input.clientId) ?? undefined,
            due_date: requireText(input.dueDate, "Due date"),
            notes: textOrNull(input.notes) ?? undefined,
          },
        }),
      );

      const references = documentReferenceFields(input);
      const { error: referenceError } = await db
        .from("invoices")
        .update(references satisfies TablesUpdate<"invoices">)
        .eq("id", input.id)
        .eq("workspace_id", workspaceId);
      if (referenceError) throw referenceError;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("invoices", workspaceId),
      });
    },
  });

  const updateAttachment = useMutation({
    mutationFn: async (input: {
      id: string;
      attachmentPath: string | null;
      attachmentFilename: string | null;
      attachmentMimeType: string | null;
      attachmentSizeBytes: number | null;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("invoices")
        .update({
          attachment_path: input.attachmentPath,
          attachment_filename: input.attachmentFilename,
          attachment_mime_type: input.attachmentMimeType,
          attachment_size_bytes: input.attachmentSizeBytes,
        } satisfies TablesUpdate<"invoices">)
        .eq("id", input.id)
        .eq("workspace_id", workspaceId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("invoices", workspaceId),
      });
    },
  });

  const sendEmail = useMutation({
    mutationFn: async (invoiceId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      return unwrap(api.sendInvoiceEmail({ path: { id: invoiceId } }));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("invoices", workspaceId),
      });
    },
  });

  const scheduleSend = useMutation({
    mutationFn: async (input: { id: string; date: string; time: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const date = requireText(input.date, "Send date");
      const time = requireText(input.time, "Send time");
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error("Send date must be YYYY-MM-DD.");
      }
      if (!/^\d{2}:\d{2}$/.test(time)) {
        throw new Error("Send time must be HH:mm.");
      }
      const scheduledAt = `${date}T${time}:00+12:00`;
      const { error } = await db
        .from("invoices")
        .update({
          scheduled_send_at: scheduledAt,
          scheduled_send_status: "scheduled",
          scheduled_send_error: null,
        } satisfies TablesUpdate<"invoices">)
        .eq("id", input.id)
        .eq("workspace_id", workspaceId)
        .eq("status", "draft");
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("invoices", workspaceId),
      });
    },
  });

  const cancelScheduledSend = useMutation({
    mutationFn: async (invoiceId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("invoices")
        .update({
          scheduled_send_at: null,
          scheduled_send_status: "cancelled",
          scheduled_send_error: null,
        } satisfies TablesUpdate<"invoices">)
        .eq("id", invoiceId)
        .eq("workspace_id", workspaceId)
        .eq("scheduled_send_status", "scheduled");
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("invoices", workspaceId),
      });
    },
  });

  const issuePublicLink = useMutation({
    mutationFn: async (invoiceId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const data = await unwrap(
        api.issueInvoiceLink({ path: { id: invoiceId } }),
      );
      if (!data.url) {
        throw new Error("MintStint could not prepare a client invoice link.");
      }
      return data;
    },
  });

  return {
    ...query,
    cancelScheduledSend,
    create,
    issuePublicLink,
    scheduleSend,
    sendEmail,
    update,
    updateAttachment,
    updateStatus,
  };
}

export function useWorkspaceInvoiceLifecycle(
  workspaceId: string | undefined,
  invoiceIds: string[],
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("invoice-lifecycle", workspaceId),
    queryFn: async () => {
      if (!workspaceId || invoiceIds.length === 0) {
        return {
          amendments: [] as InvoiceAmendment[],
          payments: [] as InvoicePayment[],
          refunds: [] as InvoiceRefund[],
        };
      }

      const [
        { data: amendments, error: amendmentsError },
        { data: payments, error: paymentsError },
        { data: refunds, error: refundsError },
      ] = await Promise.all([
        db
          .from("invoice_amendments")
          .select("*")
          .in("invoice_id", invoiceIds)
          .order("created_at", { ascending: false }),
        db
          .from("invoice_payments")
          .select("*")
          .in("invoice_id", invoiceIds)
          .order("paid_at", { ascending: false })
          .order("created_at", { ascending: false }),
        db
          .from("invoice_refunds")
          .select("*")
          .in("invoice_id", invoiceIds)
          .order("refunded_at", { ascending: false }),
      ]);

      if (amendmentsError) throw amendmentsError;
      if (paymentsError) throw paymentsError;
      if (refundsError) throw refundsError;

      return {
        amendments: (amendments ?? []) as InvoiceAmendment[],
        payments: (payments ?? []) as InvoicePayment[],
        refunds: (refunds ?? []) as InvoiceRefund[],
      };
    },
    enabled: Boolean(workspaceId),
  });

  const createRefund = useMutation({
    mutationFn: async (input: {
      invoice: Invoice;
      amount: string;
      reason?: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoice.id)) {
        throw new Error("Choose an invoice in this workspace.");
      }
      const amount = Number(input.amount);
      const amountCents =
        Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
      if (amountCents <= 0) throw new Error("Refund amount is required.");

      await unwrap(
        api.recordInvoiceRefund({
          path: { id: input.invoice.id },
          body: {
            amount_cents: amountCents,
            reason: textOrNull(input.reason) ?? undefined,
          },
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoice-lifecycle", workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  const createPayment = useMutation({
    mutationFn: async (input: {
      invoice: Invoice;
      amount: string;
      paidAt?: string;
      reference?: string;
      note?: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoice.id)) {
        throw new Error("Choose an invoice in this workspace.");
      }
      const amount = Number(input.amount);
      const amountCents =
        Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
      if (amountCents <= 0) throw new Error("Payment amount is required.");

      const paidAt = requireText(input.paidAt, "Payment date");
      await unwrap(
        api.recordInvoicePayment({
          path: { id: input.invoice.id },
          body: {
            amount_cents: amountCents,
            note: textOrNull(input.note) ?? undefined,
            paid_at: `${paidAt}T12:00:00+13:00`,
            reference: textOrNull(input.reference) ?? undefined,
          },
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoice-lifecycle", workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  return {
    ...query,
    amendments: query.data?.amendments ?? [],
    createPayment,
    createRefund,
    payments: query.data?.payments ?? [],
    refunds: query.data?.refunds ?? [],
  };
}

export function useWorkspaceInvoiceItems(
  workspaceId: string | undefined,
  invoiceIds: string[],
) {
  const queryClient = useQueryClient();
  const invoiceKey = invoiceIds.join(",");
  const query = useQuery({
    queryKey: [...rootKey, "invoice-items", workspaceId ?? "", invoiceKey],
    queryFn: async () => {
      if (!workspaceId || invoiceIds.length === 0) return [] as InvoiceItem[];
      const { data, error } = await db
        .from("invoice_items")
        .select("*")
        .in("invoice_id", invoiceIds)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as InvoiceItem[];
    },
    enabled: Boolean(workspaceId),
  });

  const createManualItem = useMutation({
    mutationFn: async (input: {
      invoiceId: string;
      description: string;
      quantity: string;
      unitAmount: string;
      gstRate: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoiceId)) {
        throw new Error("Choose an invoice in this workspace.");
      }

      const quantity = parsePositiveQuantity(input.quantity);
      const unitAmount = parsePositiveAmount(input.unitAmount, "Rate");
      const gstRate = parseGstRate(input.gstRate);

      const { error } = await db.from("invoice_items").insert({
        invoice_id: input.invoiceId,
        source: "manual",
        description: requireText(input.description, "Description"),
        quantity,
        unit_amount_cents: Math.round(unitAmount * 100),
        gst_rate: gstRate,
      } satisfies TablesInsert<"invoice_items">);
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            ...rootKey,
            "invoice-items",
            workspaceId ?? "",
            invoiceKey,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  const createFromTimeEntry = useMutation({
    mutationFn: async (input: {
      invoiceId: string;
      timeEntry: TimeEntry;
      gstRate: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoiceId)) {
        throw new Error("Choose an invoice in this workspace.");
      }
      if (input.timeEntry.invoice_id || input.timeEntry.invoiced_at) {
        throw new Error("This time entry has already been invoiced.");
      }
      if (!input.timeEntry.billable) {
        throw new Error("Only billable time can be added to an invoice.");
      }

      const gstRate = parseGstRate(input.gstRate);
      await unwrap(
        api.addTimeEntryToInvoice({
          path: { id: input.invoiceId },
          body: { time_entry_id: input.timeEntry.id, gst_rate: gstRate },
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            ...rootKey,
            "invoice-items",
            workspaceId ?? "",
            invoiceKey,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("time-entries", workspaceId),
        }),
      ]);
    },
  });

  const createFromExpense = useMutation({
    mutationFn: async (input: {
      expense: Tables<"expenses">;
      gstRate: string;
      invoiceId: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoiceId)) {
        throw new Error("Choose an invoice in this workspace.");
      }
      const gstRate = parseGstRate(input.gstRate);
      await unwrap(
        api.addExpenseToInvoice({
          path: { id: input.invoiceId },
          body: { expense_id: input.expense.id, gst_rate: gstRate },
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            ...rootKey,
            "invoice-items",
            workspaceId ?? "",
            invoiceKey,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("rebillable-expenses", workspaceId),
        }),
      ]);
    },
  });

  const updateManualItem = useMutation({
    mutationFn: async (input: {
      id: string;
      invoiceId: string;
      description: string;
      quantity: string;
      unitAmount: string;
      gstRate: string;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoiceId)) {
        throw new Error("Choose an invoice in this workspace.");
      }

      const quantity = parsePositiveQuantity(input.quantity);
      const unitAmount = parsePositiveAmount(input.unitAmount, "Rate");
      const gstRate = parseGstRate(input.gstRate);
      const { error } = await db
        .from("invoice_items")
        .update({
          description: requireText(input.description, "Description"),
          gst_rate: gstRate,
          quantity,
          unit_amount_cents: Math.round(unitAmount * 100),
        } satisfies TablesUpdate<"invoice_items">)
        .eq("id", input.id)
        .eq("invoice_id", input.invoiceId)
        .eq("source", "manual")
        .is("expense_id", null);
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            ...rootKey,
            "invoice-items",
            workspaceId ?? "",
            invoiceKey,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  const deleteManualItem = useMutation({
    mutationFn: async (input: { id: string; invoiceId: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      if (!invoiceIds.includes(input.invoiceId)) {
        throw new Error("Choose an invoice in this workspace.");
      }
      const { error } = await db
        .from("invoice_items")
        .delete()
        .eq("id", input.id)
        .eq("invoice_id", input.invoiceId)
        .eq("source", "manual")
        .is("expense_id", null);
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            ...rootKey,
            "invoice-items",
            workspaceId ?? "",
            invoiceKey,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceKey("invoices", workspaceId),
        }),
      ]);
    },
  });

  return {
    ...query,
    items: query.data ?? [],
    createManualItem,
    createFromTimeEntry,
    createFromExpense,
    deleteManualItem,
    updateManualItem,
  };
}

export function useWorkspaceRebillableExpenses(
  workspaceId: string | undefined,
) {
  const query = useQuery({
    queryKey: workspaceKey("rebillable-expenses", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as Tables<"expenses">[];
      const { data, error } = await db
        .from("expenses")
        .select("*")
        .eq("workspace_id", workspaceId)
        .eq("rebillable", true)
        .is("rebilled_invoice_id", null)
        .order("expense_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Tables<"expenses">[];
    },
    enabled: Boolean(workspaceId),
  });
  return { ...query, expenses: query.data ?? [] };
}
